<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="waitingForServerResponse"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				{{ dialogTitle }}
			</v-card-title>
			<v-divider />
			<div class="dialog-body sgnl-paragraph-s-regular sgnl-text-color-secondary">
				<div
					v-if="isCheckingMounts"
					class="sgnl-flex sgnl-align-center sgnl-gap-8"
				>
					<v-progress-circular
						indeterminate
						size="20"
						width="2"
						class="sgnl-icon-color-brand"
					/>
					<span>Checking data drives&hellip;</span>
				</div>
				<template v-else-if="hasMountingDrives">
					<p>This bucket is mounted on the following data drive(s):</p>
					<ul class="ul-list">
						<li
							v-for="drive in affectedDrives"
							:key="drive.DriveID"
						>
							<DotMark16 class="ul-icon" />{{ drive.Name }}
						</li>
					</ul>
					<p>Unmount it from each drive before disconnecting.</p>
				</template>
				<template v-else>
					<p>Would you like to disconnect S3 bucket "{{ bucketData.Name }}"?</p>
					<v-alert
						:icon="false"
						class="sgnl-paragraph-s-regular"
						type="warning"
						variant="outlined"
						elevation="0"
					>
						<strong>This operation can not be undone.</strong>
					</v-alert>
					<v-alert
						v-if="mountCheckFailed"
						:icon="false"
						border="start"
						class="sgnl-paragraph-s-regular"
						type="info"
						variant="outlined"
						elevation="0"
					>
						Could not verify whether this bucket is mounted on any data drive. Proceed with caution.
					</v-alert>
					<v-alert
						v-if="showError"
						:icon="false"
						border="start"
						class="sgnl-paragraph-s-regular"
						type="warning"
						variant="outlined"
						elevation="0"
					>
						{{ errorMessage }}
					</v-alert>
				</template>
			</div>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
				>
					Close
				</v-btn>
				<v-btn
					variant="flat"
					color="error"
					:disabled="hasMountingDrives || isCheckingMounts"
					:loading="waitingForServerResponse"
					@click="commitDisconnect"
				>
					Disconnect
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { defineComponent } from "vue";
import { mapActions } from "pinia";
import { sdk } from "@/js/sdk";
import { SdkError } from "@signaloid/cloud-compute-engine-client";
import axios from "axios";
import { useDataSourcesStore } from "@/stores/dataSources";
import { monitoringCaptureError } from "@/plugins/monitoring";
import DotMark16 from "@carbon/icons-vue/es/dot-mark/16";

export default defineComponent({
	name: "DisconnectBucketDialog",
	components: { DotMark16 },
	emits: ["update:showDialog", "bucket-disconnected", "close-dialog"],
	props: {
		showDialog: Boolean,
		bucketData: Object,
	},
	data: () => ({
		waitingForServerResponse: false,
		errorMessage: "unknown error",
		showError: false,
		isCheckingMounts: false,
		affectedDrives: [],
		mountCheckFailed: false,
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		hasMountingDrives() {
			return this.affectedDrives.length > 0;
		},
		dialogTitle() {
			return this.hasMountingDrives
				? `Cannot disconnect "${this.bucketData.Name}"`
				: `Disconnect "${this.bucketData.Name}"?`;
		},
	},
	mounted() {
		this.checkMounts();
	},
	methods: {
		...mapActions(useDataSourcesStore, ["findDrivesMountingResource"]),
		async checkMounts() {
			this.isCheckingMounts = true;
			this.mountCheckFailed = false;
			try {
				const { drives, allChecked } = await this.findDrivesMountingResource(this.bucketData.BucketID);
				this.affectedDrives = drives;
				this.mountCheckFailed = !allChecked;
			} catch (error) {
				monitoringCaptureError(error, "Check bucket mount drives");
				this.mountCheckFailed = true;
				this.affectedDrives = [];
			} finally {
				this.isCheckingMounts = false;
			}
		},
		async commitDisconnect() {
			this.errorMessage = undefined;
			this.showError = false;
			try {
				this.waitingForServerResponse = true;
				await sdk.buckets.delete(this.bucketData.BucketID);
				this.$emit("bucket-disconnected");
				this.closeDialog();
			} catch (error) {
				monitoringCaptureError(error, "Disconnect bucket");
				if (error instanceof SdkError) {
					this.errorMessage = "An error occurred while disconnecting the bucket.";
				} else if (axios.isAxiosError(error)) {
					this.errorMessage =
						"Failed to disconnect the bucket. Make sure you have an active internet connection.";
				} else {
					this.errorMessage = "An error occurred while disconnecting the bucket.";
				}
				this.showError = true;
			} finally {
				this.waitingForServerResponse = false;
			}
		},
		handleDismiss() {
			if (this.waitingForServerResponse) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("close-dialog");
		},
	},
});
</script>
